import { Link } from "react-router-dom";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Tooltip,
    Chip,
} from "@heroui/react";
import { LuEye, LuPencil, LuTrash, LuPlus, LuTruck } from "react-icons/lu";
import { useDataTable } from "@/hooks";
import {
    EmptyState,
    ThemedText,
    PageHeader,
    ErrorState,
} from "@/components/ui";
import {
    ActiveFilterChips,
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    DeleteConfirmModal,
    FilterButton,
} from "@/components/data-table";
import { FilterData } from "./components";
import { formatPhoneInternational } from "@/utils";
import { useUser } from "@/context/UserContext";

// Initial filter values
const initialFilters = {
    name: "",
    address: "",
    contact: "",
};

const SupplierData = () => {
    const { hasPermission } = useUser();
    const table = useDataTable(
        "suppliers/data",
        "suppliers/{id}/delete",
        {},
        initialFilters,
    );

    // Filter labels
    const filterLabels = {
        name: { label: "Nama Supplier" },
        address: { label: "Alamat" },
        contact: { label: "Kontak" },
    };

    // Handle remove single filter
    const handleRemoveFilter = (key) => {
        table.filterPanel.setPending((prev) => ({
            ...prev,
            [key]: initialFilters[key],
        }));
        table.filters.setFilter(key, initialFilters[key]);
    };

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Data"
                    description="Terjadi kesalahan saat memuat data supplier. Silakan coba lagi."
                    onAction={table.refetch}
                />
            </div>
        );
    }

    const skeletonColumns = [
        { name: "No.", skeletonWidth: "w-8" },
        { name: "Nama Supplier", skeletonWidth: "w-48" },
        { name: "Alamat", skeletonWidth: "w-48" },
        { name: "Kontak", skeletonWidth: "w-32" },
        { name: "Status Keaktifkan", skeletonWidth: "w-24" },
        { name: "Produk yang dijual", skeletonWidth: "w-40" },
        { name: "Aksi", skeletonWidth: "w-20", align: "end" },
    ];

    return (
        <div className="flex h-full w-full flex-col gap-4">
            {/* Page Header */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Supplier"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Supplier" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={table.filterPanel.isOpen}
                        onToggle={() =>
                            table.filterPanel.setIsOpen(
                                !table.filterPanel.isOpen,
                            )
                        }
                        activeCount={table.filters.activeCount}
                    />
                    {hasPermission("supplier.create") && (
                        <Button
                            as={Link}
                            to="/suppliers/create"
                            color="primary"
                            startContent={<LuPlus />}
                            size="sm"
                        >
                            Tambah Supplier
                        </Button>
                    )}
                </div>
            </div>

            {/* Filter Panel */}
            <FilterData table={table} initialFilters={initialFilters} />

            {/* Active Filter Chips */}
            <ActiveFilterChips
                filters={table.filters.values}
                filterLabels={filterLabels}
                onRemove={handleRemoveFilter}
            />

            {/* Data Table */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Belum Ada Supplier"
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada supplier yang cocok dengan filter."
                                    : "Mulai dengan menambahkan supplier baru."
                            }
                            icon={LuTruck}
                        />
                    ) : (
                        <Table aria-label="Tabel Gudang" shadow="none">
                            <TableHeader>
                                <TableColumn key="no" width={50}>
                                    No.
                                </TableColumn>
                                <TableColumn key="name">
                                    Nama Supplier
                                </TableColumn>
                                <TableColumn key="address">Alamat</TableColumn>
                                <TableColumn key="contact">Kontak</TableColumn>
                                <TableColumn key="status">
                                    Status Keaktifkan
                                </TableColumn>
                                <TableColumn key="products_summary">
                                    Produk yang dijual
                                </TableColumn>
                                <TableColumn align="end" key="actions">
                                    Aksi
                                </TableColumn>
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>
                                            <ThemedText
                                                size="sm"
                                                variant="secondary"
                                            >
                                                {(table.pagination.page - 1) *
                                                    table.pagination.perPage +
                                                    table.items.indexOf(item) +
                                                    1}
                                                .
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <ThemedText size="sm">
                                                    {item.name}
                                                </ThemedText>
                                                {item.description && (
                                                    <ThemedText
                                                        size="xs"
                                                        variant="secondary"
                                                        className="line-clamp-1"
                                                    >
                                                        {item.description}
                                                    </ThemedText>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText
                                                size="xs"
                                                variant="secondary"
                                                className="line-clamp-2"
                                            >
                                                {item.first_address
                                                    ?.full_address || "-"}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <ThemedText
                                                    size="sm"
                                                    className="whitespace-nowrap"
                                                >
                                                    {formatPhoneInternational(
                                                        item
                                                            .first_contact_number
                                                            ?.number,
                                                    )}
                                                </ThemedText>
                                                <ThemedText
                                                    size="xs"
                                                    variant="secondary"
                                                >
                                                    {item.first_contact_number
                                                        ?.contact_type?.name ||
                                                        ""}
                                                </ThemedText>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                color={
                                                    item.active_status
                                                        ?.badge_color
                                                }
                                                size="sm"
                                                className="font-medium"
                                                variant="flat"
                                            >
                                                {item.active_status?.name ||
                                                    "-"}
                                            </Chip>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText
                                                size="xs"
                                                variant="secondary"
                                                className="line-clamp-2"
                                            >
                                                {item.products_summary || "-"}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <div className="relative flex items-center justify-end gap-2">
                                                <Tooltip content="Detail Supplier">
                                                    <Button
                                                        isIconOnly
                                                        size="sm"
                                                        variant="light"
                                                        as={Link}
                                                        to={`/suppliers/${item.id}/detail`}
                                                    >
                                                        <LuEye
                                                            size={14}
                                                            className="text-default-500"
                                                        />
                                                    </Button>
                                                </Tooltip>
                                                {hasPermission("supplier.edit") && (
                                                    <Tooltip content="Edit Supplier">
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            variant="light"
                                                            as={Link}
                                                            to={`/suppliers/${item.id}/edit`}
                                                        >
                                                            <LuPencil
                                                                size={14}
                                                                className="text-default-500"
                                                            />
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                                {hasPermission("supplier.delete") && (
                                                    <Tooltip content="Hapus Supplier">
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            color="danger"
                                                            variant="light"
                                                            onPress={() =>
                                                                table.deleteState.openDeleteModal(
                                                                    item.id,
                                                                )
                                                            }
                                                        >
                                                            <LuTrash size={14} />
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>

            <DeleteConfirmModal
                table={table}
                title="Hapus Supplier"
                description="Apakah Anda yakin ingin menghapus supplier ini? Tindakan ini tidak dapat dibatalkan."
            />
        </div>
    );
};

export default SupplierData;
